import React, { useEffect, useMemo, useState } from "react";
import { MaleCalculatorForm } from "@/components/MaleCalculatorForm";
import { ResultsDisplay } from "@/components/ResultsDisplay";
import { calculateMaleCompatibility, MaleFormData } from "@/utils/maleCalculator";
import { maleDataSources } from "@/data/maleDemographicData";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import EmailCapture from "@/components/EmailCapture";
import SponsorPlacement from "@/components/SponsorPlacement";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Link, useLocation } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { getRouteMeta, canonicalUrl } from "@/seo/routeMeta";
import { encodeMaleParams, decodeMaleParams } from "@/utils/shareParams";
import { trackEvent, probabilityBucket } from "@/lib/analytics";
import { SITE_URL } from "@/seo/routeMeta";

const PATH = "/male-partner-calculator";

const MalePartnerCalculator = () => {
  const location = useLocation();
  const meta = getRouteMeta(PATH);

  useSEO({
    title: meta?.title ?? "Male Partner Calculator",
    description: meta?.description ?? "",
    keywords: meta?.keywords,
    canonical: canonicalUrl(PATH),
    structuredData: meta?.structuredData,
  });

  const sharedFormData = useMemo(
    () => decodeMaleParams(location.search),
    [location.search]
  );

  const [results, setResults] = useState<ReturnType<typeof calculateMaleCompatibility> | null>(null);
  const [shareUrl, setShareUrl] = useState<string>("");

  const runCalculation = (formData: MaleFormData, source: "form" | "shared_link") => {
    const calculationResults = calculateMaleCompatibility(formData);
    setResults(calculationResults);

    const query = encodeMaleParams(formData);
    setShareUrl(`${SITE_URL}${PATH}?${query}`);
    window.history.replaceState(null, "", `${window.location.pathname}?${query}`);

    trackEvent("calculation_completed", {
      calculator: "men",
      probability_bucket: probabilityBucket(calculationResults.probability),
      source,
    });
  };

  useEffect(() => {
    if (sharedFormData) {
      runCalculation(sharedFormData, "shared_link");
    }
    // Run once on mount for shared links only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <Breadcrumbs />

      {/* Hero Section */}
      <header className="border-b bg-gradient-to-b from-background to-muted/20">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center space-y-6 max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Male Partner Calculator
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              How many US men actually match your standards? Combine height, income,
              education, and more — every filter backed by US Census Bureau, CDC, and
              Bureau of Labor Statistics data.
            </p>
            <p className="text-sm text-muted-foreground">
              Evaluating standards for women instead?{" "}
              <Link to="/" className="text-primary hover:underline font-medium">
                Try the Female Partner Calculator
              </Link>
            </p>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-12">
        <MaleCalculatorForm
          key={sharedFormData ? "shared" : "default"}
          initialData={sharedFormData ?? undefined}
          onCalculate={(data) => runCalculation(data, "form")}
        />

        {results && (
          <ResultsDisplay {...results} noun="men" shareUrl={shareUrl} />
        )}

        {results && <SponsorPlacement placement="post_results_male" />}
        {results && <EmailCapture />}

        {/* Context section */}
        <section className="max-w-4xl mx-auto space-y-6">
          <h2 className="text-2xl font-bold">Why the numbers shrink so fast</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              Each preference acts as an independent statistical filter on the US male
              population. Only about 14.5% of US men are 6'0" or taller, and roughly
              one in five men aged 25-44 earns $100,000 or more per year. Multiply
              those two filters together and you have already excluded about 97% of
              the population — before adding age, education, or marital status.
            </p>
            <p>
              This is the same multiplicative probability model used by our{" "}
              <Link to="/" className="text-primary hover:underline">
                Female Partner Calculator
              </Link>
              , applied to male demographic data. Curious how realistic your criteria
              are overall? Put them through our{" "}
              <Link to="/dating-standards-calculator" className="text-primary hover:underline">
                dating standards calculator
              </Link>
              . Read the full{" "}
              <Link to="/methodology" className="text-primary hover:underline">
                methodology
              </Link>{" "}
              for the math, assumptions, and limitations, or browse our{" "}
              <Link to="/stats" className="text-primary hover:underline">
                US dating statistics hub
              </Link>{" "}
              for the underlying research.
            </p>
          </div>
        </section>

        {/* Data Sources */}
        <section className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6">Data Sources</h2>
          <div className="grid gap-4">
            {maleDataSources.map((source) => (
              <div key={source.id} className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">
                  [{source.id}] {source.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-2">
                  {source.description}
                </p>
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline text-sm"
                >
                  {source.url}
                </a>
              </div>
            ))}
          </div>
        </section>

        {/* Disclaimer */}
        <section className="max-w-4xl mx-auto">
          <div className="border-l-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-r-lg">
            <h3 className="font-semibold mb-4">Statistical Disclaimer</h3>
            <div className="text-sm space-y-2">
              <p>
                This calculator provides statistical probabilities based on
                demographic data and should not be interpreted as definitive
                predictions about individual relationships or dating outcomes.
              </p>
              <p>
                Results reflect population-level statistics and do not account for
                geography, social circles, or individual compatibility beyond the
                measured criteria. Filters are treated as statistically independent,
                which slightly overstates rarity when preferences are correlated
                (for example, height and income).
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MalePartnerCalculator;
